"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { createChart, CandlestickSeries, LineSeries, HistogramSeries, ColorType, CrosshairMode, LineStyle, type CandlestickData, type HistogramData, type IChartApi, type ISeriesApi, type LineData, type Time } from "lightweight-charts";
import { sma, rsi, type OHLCV } from "@/lib/indicators";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Timeframe = "1D" | "1W" | "1M" | "3M" | "1Y" | "ALL";
interface Candle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}
interface DevScorePoint {
  time: number;
  value: number;
}
interface AdvancedPriceChartProps {
  candles: Candle[];
  devScore: DevScorePoint[];
  timeframe: Timeframe;
  onTimeframeChange: (timeframe: Timeframe) => void;
  isLoading?: boolean;
}
interface OverlayState {
  ma7: boolean;
  ma30: boolean;
  rsi: boolean;
  devScore: boolean;
}
const TIMEFRAMES: Timeframe[] = ["1D", "1W", "1M", "3M", "1Y", "ALL"];

// ---------------------------------------------------------------------------
// Chart colours — resolved from the theme CSS variables at runtime so the
// chart canvas tracks light and dark exactly like the surrounding markup.
// ---------------------------------------------------------------------------

const SERIES_COLORS = {
  upColor: "#22c55e",
  downColor: "#ef4444",
  volumeUp: "rgba(34, 197, 94, 0.25)",
  volumeDown: "rgba(239, 68, 68, 0.25)",
  ma7: "#38bdf8",
  ma30: "#f59e0b",
  rsi: "#a78bfa",
  devScore: "#ec4899"
};
interface ThemeColors {
  background: string;
  text: string;
  grid: string;
  border: string;
  crosshair: string;
}

/** Read the live theme tokens off the document so the canvas matches the DOM. */
function readThemeColors(element: HTMLElement): ThemeColors {
  const styles = getComputedStyle(element);
  const token = (name: string, fallback: string) => {
    const value = styles.getPropertyValue(name).trim();
    return value ? `hsl(${value})` : fallback;
  };
  const isLight = document.documentElement.classList.contains("light");
  return {
    background: token("--card", "hsl(230, 15%, 8%)"),
    text: token("--muted-foreground", "#9ca3af"),
    grid: isLight ? "rgba(0, 0, 0, 0.06)" : "rgba(255, 255, 255, 0.04)",
    border: isLight ? "rgba(0, 0, 0, 0.10)" : "rgba(255, 255, 255, 0.08)",
    crosshair: isLight ? "rgba(0, 0, 0, 0.25)" : "rgba(255, 255, 255, 0.15)"
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPrice(price: number): string {
  if (price < 0.0001) return price.toFixed(8);
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  if (price < 100) return price.toFixed(3);
  return price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
function formatCompact(value: number): string {
  if (value >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
  return value.toFixed(2);
}
const DEFAULT_OVERLAYS: OverlayState = {
  ma7: false,
  ma30: false,
  rsi: false,
  devScore: true
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdvancedPriceChart(t0) {
  const $ = _c(65);
  const {
    candles,
    devScore,
    timeframe,
    onTimeframeChange,
    isLoading: t1
  } = t0;
  const isLoading = t1 === undefined ? false : t1;
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const seriesRefs = useRef(t2);
  const [overlays, setOverlays] = useState(DEFAULT_OVERLAYS);
  const [themeVersion, setThemeVersion] = useState(0);
  let t3;
  if ($[1] !== candles) {
    t3 = candles.map(_temp);
    $[1] = candles;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const ohlcv = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = key => setOverlays(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const toggle = t4;
  let t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const observer = new MutationObserver(() => setThemeVersion(_temp2));
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"]
      });
      return () => observer.disconnect();
    };
    t6 = [];
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  if ($[6] !== candles || $[7] !== devScore || $[8] !== ohlcv || $[9] !== overlays.devScore || $[10] !== overlays.ma30 || $[11] !== overlays.ma7 || $[12] !== overlays.rsi || $[13] !== timeframe) {
    t7 = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const colors = readThemeColors(container);
      const chart = createChart(container, {
        layout: {
          background: {
            type: ColorType.Solid,
            color: colors.background
          },
          textColor: colors.text,
          fontSize: 12
        },
        grid: {
          vertLines: {
            color: colors.grid
          },
          horzLines: {
            color: colors.grid
          }
        },
        crosshair: {
          mode: CrosshairMode.Normal,
          vertLine: {
            color: colors.crosshair,
            labelBackgroundColor: "#374151"
          },
          horzLine: {
            color: colors.crosshair,
            labelBackgroundColor: "#374151"
          }
        },
        rightPriceScale: {
          borderColor: colors.border,
          scaleMargins: {
            top: 0.1,
            bottom: overlays.rsi ? 0.4 : 0.25
          }
        },
        timeScale: {
          borderColor: colors.border,
          timeVisible: timeframe === "1D" || timeframe === "1W",
          secondsVisible: false
        },
        handleScroll: {
          vertTouchDrag: false
        },
        autoSize: true
      });
      chartRef.current = chart;
      seriesRefs.current = [];
      const track = series => {
        seriesRefs.current.push(series);
        return series;
      };
      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: SERIES_COLORS.upColor,
        downColor: SERIES_COLORS.downColor,
        borderUpColor: SERIES_COLORS.upColor,
        borderDownColor: SERIES_COLORS.downColor,
        wickUpColor: SERIES_COLORS.upColor,
        wickDownColor: SERIES_COLORS.downColor
      });
      candleSeries.setData(candles.map(_temp3) as CandlestickData<Time>[]);
      track(candleSeries as unknown as ISeriesApi<never>);
      const volumeSeries = chart.addSeries(HistogramSeries, {
        priceFormat: {
          type: "volume"
        },
        priceScaleId: "volume"
      });
      volumeSeries.priceScale().applyOptions({
        scaleMargins: {
          top: 0.82,
          bottom: 0
        }
      });
      volumeSeries.setData(candles.map(_temp4) as HistogramData<Time>[]);
      track(volumeSeries as unknown as ISeriesApi<never>);
      const addMovingAverage = (period, color) => {
        const points = sma(ohlcv, period);
        if (points.length === 0) {
          return;
        }
        const series_0 = chart.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false
        });
        series_0.setData(points.map(_temp5) as LineData<Time>[]);
        track(series_0 as unknown as ISeriesApi<never>);
      };
      if (overlays.ma7) {
        addMovingAverage(7, SERIES_COLORS.ma7);
      }
      if (overlays.ma30) {
        addMovingAverage(30, SERIES_COLORS.ma30);
      }
      if (overlays.rsi) {
        const points_0 = rsi(ohlcv, 14);
        if (points_0.length > 0) {
          const series_1 = chart.addSeries(LineSeries, {
            color: SERIES_COLORS.rsi,
            lineWidth: 2,
            priceScaleId: "rsi",
            priceLineVisible: false,
            lastValueVisible: true
          });
          series_1.priceScale().applyOptions({
            scaleMargins: {
              top: 0.68,
              bottom: 0.12
            }
          });
          series_1.setData(points_0.map(_temp6) as LineData<Time>[]);
          series_1.createPriceLine({
            price: 70,
            color: SERIES_COLORS.downColor,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: false,
            title: ""
          });
          series_1.createPriceLine({
            price: 30,
            color: SERIES_COLORS.upColor,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: false,
            title: ""
          });
          track(series_1 as unknown as ISeriesApi<never>);
        }
      }
      if (overlays.devScore && devScore.length > 0) {
        const series_2 = chart.addSeries(LineSeries, {
          color: SERIES_COLORS.devScore,
          lineWidth: 2,
          priceScaleId: "devScore",
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: true,
          priceFormat: {
            type: "custom",
            formatter: _temp7
          }
        });
        series_2.priceScale().applyOptions({
          scaleMargins: {
            top: 0.1,
            bottom: 0.25
          }
        });
        series_2.setData(devScore.map(_temp8) as LineData<Time>[]);
        track(series_2 as unknown as ISeriesApi<never>);
      }
      chart.timeScale().fitContent();
      return () => {
        chart.remove();
        chartRef.current = null;
        seriesRefs.current = [];
      };
    };
    $[6] = candles;
    $[7] = devScore;
    $[8] = ohlcv;
    $[9] = overlays.devScore;
    $[10] = overlays.ma30;
    $[11] = overlays.ma7;
    $[12] = overlays.rsi;
    $[13] = timeframe;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== candles || $[16] !== devScore || $[17] !== ohlcv || $[18] !== overlays || $[19] !== themeVersion || $[20] !== timeframe) {
    t8 = [candles, devScore, ohlcv, overlays, timeframe, themeVersion];
    $[15] = candles;
    $[16] = devScore;
    $[17] = ohlcv;
    $[18] = overlays;
    $[19] = themeVersion;
    $[20] = timeframe;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useEffect(t7, t8);
  const latest = candles.length > 0 ? candles[candles.length - 1] : null;
  const first = candles.length > 0 ? candles[0] : null;
  const changePercent = latest && first && first.open > 0 ? (latest.close - first.open) / first.open * 100 : null;
  let t9;
  if ($[22] !== changePercent || $[23] !== latest) {
    t9 = <div className="flex items-baseline gap-3">{latest ? <><span className="font-mono text-xl font-semibold text-[hsl(var(--foreground))]">${formatPrice(latest.close)}</span>{changePercent != null && <span className={`font-mono text-sm ${changePercent >= 0 ? "text-emerald-400" : "text-red-400"}`}>{changePercent >= 0 ? "+" : ""}{changePercent.toFixed(2)}%</span>}</> : <span className="text-sm text-gray-500">No price history yet</span>}</div>;
    $[22] = changePercent;
    $[23] = latest;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== onTimeframeChange || $[26] !== timeframe) {
    t10 = TIMEFRAMES.map(tf => <button key={tf} type="button" onClick={() => onTimeframeChange(tf)} aria-pressed={tf === timeframe} className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${tf === timeframe ? "bg-blue-600 text-white" : "border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"}`}>{tf}</button>);
    $[25] = onTimeframeChange;
    $[26] = timeframe;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <div className="flex flex-wrap gap-1" role="group" aria-label="Chart timeframe">{t10}</div>;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t9) {
    t12 = <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[hsl(var(--border))] px-4 py-3">{t9}{t11}</div>;
    $[30] = t11;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => toggle("ma7");
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== overlays.ma7) {
    t14 = <OverlayToggle label="MA7" color={SERIES_COLORS.ma7} active={overlays.ma7} onClick={t13} />;
    $[34] = overlays.ma7;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => toggle("ma30");
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== overlays.ma30) {
    t16 = <OverlayToggle label="MA30" color={SERIES_COLORS.ma30} active={overlays.ma30} onClick={t15} />;
    $[37] = overlays.ma30;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => toggle("rsi");
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== overlays.rsi) {
    t18 = <OverlayToggle label="RSI" color={SERIES_COLORS.rsi} active={overlays.rsi} onClick={t17} />;
    $[40] = overlays.rsi;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => toggle("devScore");
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const t20 = devScore.length === 0;
  let t21;
  if ($[43] !== overlays.devScore || $[44] !== t20) {
    t21 = <OverlayToggle label="Dev activity" color={SERIES_COLORS.devScore} active={overlays.devScore} onClick={t19} disabled={t20} />;
    $[43] = overlays.devScore;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="ml-auto hidden text-xs text-[hsl(var(--muted-foreground))] sm:block">Dev activity plotted against price</span>;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t14 || $[48] !== t16 || $[49] !== t18 || $[50] !== t21) {
    t23 = <div className="flex flex-wrap items-center gap-2 border-b border-[hsl(var(--border))] px-4 py-2">{t14}{t16}{t18}{t21}{t22}</div>;
    $[47] = t14;
    $[48] = t16;
    $[49] = t18;
    $[50] = t21;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div ref={containerRef} className="h-[320px] w-full sm:h-[420px] lg:h-[480px]" />;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== isLoading) {
    t25 = isLoading && <div className="absolute inset-0 flex items-center justify-center bg-[hsl(var(--card))]/60 text-sm text-[hsl(var(--muted-foreground))]">Loading chart data</div>;
    $[53] = isLoading;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== candles.length || $[56] !== isLoading) {
    t26 = !isLoading && candles.length === 0 && <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">No price history available for this timeframe.</div>;
    $[55] = candles.length;
    $[56] = isLoading;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t25 || $[59] !== t26) {
    t27 = <div className="relative">{t24}{t25}{t26}</div>;
    $[58] = t25;
    $[59] = t26;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t12 || $[62] !== t23 || $[63] !== t27) {
    t28 = <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">{t12}{t23}{t27}</div>;
    $[61] = t12;
    $[62] = t23;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  return t28;
}

// ---------------------------------------------------------------------------
// Overlay toggle button
// ---------------------------------------------------------------------------
function _temp8(p_1) {
  return {
    time: p_1.time as Time,
    value: p_1.value
  };
}
function _temp7(value) {
  return formatCompact(value);
}
function _temp6(p_0) {
  return {
    time: p_0.time as Time,
    value: p_0.value
  };
}
function _temp5(p) {
  return {
    time: p.time as Time,
    value: p.value
  };
}
function _temp4(c_1) {
  return {
    time: c_1.time as Time,
    value: c_1.volume,
    color: c_1.close >= c_1.open ? SERIES_COLORS.volumeUp : SERIES_COLORS.volumeDown
  };
}
function _temp3(c_0) {
  return {
    time: c_0.time as Time,
    open: c_0.open,
    high: c_0.high,
    low: c_0.low,
    close: c_0.close
  };
}
function _temp2(v) {
  return v + 1;
}
function _temp(c) {
  return {
    time: c.time,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume
  };
}
interface OverlayToggleProps {
  label: string;
  color: string;
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
}
function OverlayToggle(t0) {
  const $ = _c(10);
  const {
    label,
    color,
    active,
    onClick,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const t2 = `inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${active ? "bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]"}`;
  const t3 = active ? color : "transparent";
  const t4 = `1px solid ${color}`;
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = <span className="h-2 w-2 rounded-full" style={{
      backgroundColor: t3,
      border: t4
    }} />;
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== active || $[4] !== disabled || $[5] !== label || $[6] !== onClick || $[7] !== t2 || $[8] !== t5) {
    t6 = <button type="button" onClick={onClick} disabled={disabled} aria-pressed={active} className={t2}>{t5}{label}</button>;
    $[3] = active;
    $[4] = disabled;
    $[5] = label;
    $[6] = onClick;
    $[7] = t2;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}